import * as Sentry from "@sentry/react";
import { ShieldCheck } from "lucide-react";
import { useEffect, useState, type FC } from "react";
import { toast } from "sonner";

import { ListCard, ListRow } from "@/components/ui/list-card";
import { Switch } from "@/components/ui/switch";

import { LawFirmsAPI } from "../../../api/lawFirms";
import type { LawFirm } from "../../../api/types/lawFirm";

const formatDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
};

/**
 * The Classic drafting allowlist switch. Saves on toggle through its own
 * superadmin route; it is never part of the firm's Save.
 */
export const LegacyDraftingCard: FC<{ lawFirm: LawFirm }> = ({ lawFirm }) => {
  const [enabled, setEnabled] = useState(false);
  const [enabledAt, setEnabledAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setEnabled(Boolean(lawFirm.legacy_drafting_enabled));
    setEnabledAt(lawFirm.legacy_drafting_enabled_at ?? null);
  }, [lawFirm]);

  const toggle = async (next: boolean) => {
    setSaving(true);
    setEnabled(next);
    try {
      const saved = await LawFirmsAPI.setLegacyDrafting(lawFirm.uid, next);
      setEnabled(saved.legacy_drafting_enabled);
      if (saved.legacy_drafting_enabled_at) {
        setEnabledAt(saved.legacy_drafting_enabled_at);
      }
      toast.success(
        next ? "Classic drafting is on" : "Classic drafting is off"
      );
    } catch (error) {
      Sentry.captureException(error);
      setEnabled(!next);
      toast.error("Couldn't change Classic drafting for this firm.");
    } finally {
      setSaving(false);
    }
  };

  const since = enabled ? formatDate(enabledAt) : null;
  return (
    <ListCard>
      <ListRow
        title="Classic Drafting"
        description="Offers Classic on new drafts that have a classic template, including the firm's templates and letterheads from the old app."
        control={
          <Switch
            aria-label="Classic Drafting"
            checked={enabled}
            disabled={saving}
            onCheckedChange={(next) => void toggle(next)}
          />
        }
      />
      <div className="flex items-center gap-2 px-4 py-2.5 text-sm text-fg-3">
        <ShieldCheck className="size-3.5 shrink-0" />
        <span>
          Only CaseBlink superadmins can change this.
          {since ? ` Turned on ${since}.` : ""}
        </span>
      </div>
    </ListCard>
  );
};
